import {
  Link2, Video, Music, Image, Images, Type, Heading, Minus,
  Share2, MapPin, HelpCircle, Mail, Timer, Quote,
  Instagram, Linkedin, MessageCircle, Music2, Youtube, Facebook, Twitter,
} from "lucide-react";

// ============ PLATFORM SOSIAL (dipakai blok "social" & baris ikon profil) ============
export const SOCIAL_PLATFORMS = [
  { value: "instagram", label: "Instagram", Icon: Instagram, color: "#ec4899", urlFor: (v) => `https://instagram.com/${v.replace(/^@/, "")}` },
  { value: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "#3b82f6", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://linkedin.com/in/${v}`) },
  { value: "whatsapp", label: "WhatsApp", Icon: MessageCircle, color: "#10b981", urlFor: (v) => `https://wa.me/${v.replace(/[^0-9]/g, "")}` },
  { value: "email", label: "Email", Icon: Mail, color: "#f59e0b", urlFor: (v) => `mailto:${v}` },
  { value: "tiktok", label: "TikTok", Icon: Music2, color: "#25181c", urlFor: (v) => `https://tiktok.com/@${v.replace(/^@/, "")}` },
  { value: "youtube", label: "YouTube", Icon: Youtube, color: "#ef4444", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://youtube.com/${v}`) },
  { value: "facebook", label: "Facebook", Icon: Facebook, color: "#3b5998", urlFor: (v) => (/^https?:\/\//.test(v) ? v : `https://facebook.com/${v}`) },
  { value: "twitter", label: "X / Twitter", Icon: Twitter, color: "#0f1419", urlFor: (v) => `https://x.com/${v.replace(/^@/, "")}` },
];

export const getSocialPlatform = (value) => SOCIAL_PLATFORMS.find((p) => p.value === value);

// ============ METADATA BLOK (menu "Add Block") ============
// isPro hanya penanda visual — semua blok saat ini GRATIS (belum ada gating).
export const BLOCK_TYPES = [
  { value: "link", label: "Tautan", desc: "Tombol menuju URL", Icon: Link2, isPro: false },
  { value: "video", label: "Video", desc: "YouTube / TikTok", Icon: Video, isPro: false },
  { value: "audio", label: "Musik", desc: "Spotify / SoundCloud", Icon: Music, isPro: true },
  { value: "image", label: "Gambar", desc: "Foto tunggal", Icon: Image, isPro: false },
  { value: "gallery", label: "Galeri", desc: "Kumpulan foto", Icon: Images, isPro: true },
  { value: "text", label: "Teks", desc: "Paragraf bebas", Icon: Type, isPro: false },
  { value: "heading", label: "Judul", desc: "Teks besar", Icon: Heading, isPro: false },
  { value: "divider", label: "Pembatas", desc: "Garis pemisah", Icon: Minus, isPro: false },
  { value: "social", label: "Sosial", desc: "Deret ikon media sosial", Icon: Share2, isPro: false },
  { value: "map", label: "Peta", desc: "Lokasi (embed)", Icon: MapPin, isPro: true },
  { value: "faq", label: "FAQ", desc: "Tanya-jawab", Icon: HelpCircle, isPro: true },
  { value: "newsletter", label: "Newsletter", desc: "Formulir email", Icon: Mail, isPro: true },
  { value: "countdown", label: "Countdown", desc: "Hitung mundur", Icon: Timer, isPro: true },
  { value: "quote", label: "Kutipan", desc: "Testimoni / kutipan", Icon: Quote, isPro: false },
];

export const getBlockType = (value) => BLOCK_TYPES.find((b) => b.value === value) || BLOCK_TYPES[0];

const genId = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

// Data awal untuk tiap jenis blok saat ditambahkan lewat menu "Add Block".
export const createBlock = (type) => {
  const base = { id: genId(), type, title: "", url: "", active: true };
  switch (type) {
    case "image": return { ...base, caption: "", link_url: "" };
    case "gallery": return { ...base, items: [] };
    case "text": return { ...base, content: "" };
    case "heading": return { ...base, title: "" };
    case "divider": return { ...base, label: "" };
    case "social": return { ...base, items: [] };
    case "map": return { ...base, title: "", url: "" };
    case "faq": return { ...base, items: [{ question: "", answer: "" }] };
    case "newsletter": return { ...base, title: "Berlangganan", description: "Dapatkan kabar terbaru langsung di email Anda." };
    case "countdown": return { ...base, title: "", deadline: "" };
    case "quote": return { ...base, content: "", author: "" };
    default: return base;
  }
};

// Apakah sebuah blok sudah punya isi yang cukup untuk ditampilkan/disimpan?
// Blok yang kosong (mis. link tanpa URL) tidak ikut terkirim ke backend.
export const isBlockComplete = (b) => {
  switch (b?.type) {
    case "link": return !!(b.title?.trim() && b.url?.trim());
    case "video": return !!b.url?.trim();
    case "audio": return !!b.url?.trim();
    case "image": return !!b.url?.trim();
    case "gallery": return (b.items || []).filter((x) => x?.trim()).length > 0;
    case "text": return !!b.content?.trim();
    case "heading": return !!b.title?.trim();
    case "divider": return true;
    case "social": return (b.items || []).some((s) => s?.value?.trim());
    case "map": return !!b.url?.trim();
    case "faq": return (b.items || []).some((f) => f?.question?.trim());
    case "newsletter": return true;
    case "countdown": return !!b.deadline;
    case "quote": return !!b.content?.trim();
    default: return !!(b?.title?.trim() && b?.url?.trim());
  }
};

// ============ HELPER URL MEDIA ============

// Spotify: open.spotify.com/{type}/{id} → { type, id } untuk URL embed.
export const parseSpotify = (url) => {
  if (!url) return null;
  const m = url.trim().match(/open\.spotify\.com\/(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/);
  return m ? { type: m[1], id: m[2] } : null;
};

export const isTikTokUrl = (url) => !!url && /tiktok\.com/.test(url.trim());

// SoundCloud: kirim URL apa adanya ke player embed.
export const isSoundCloudUrl = (url) => !!url && /soundcloud\.com/.test(url.trim());

// Google Maps: bila URL sudah berupa embed (google.com/maps/embed), pakai apa
// adanya; selain itu anggap sebagai teks alamat/query pencarian.
export const isMapsEmbedUrl = (url) => !!url && /(google\.com\/maps\/embed|maps\.google\.com|google\.com\/maps\/d\/embed)/.test(url.trim());
